import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Client } from '@df/core/service/client';
import { Customer } from '@df/session/api/customer';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NProfiles } from '../../../account/pwa/profiles/profile.interfaces';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { NAntavo } from '../../antavo/antavo.interface';
import { AntavoService } from '../../antavo/antavo.service';
import { NProfileMission } from './profile-mission.component';

/**
 * Resolver to load CMS block and find matching Antavo mission for the profile mission page
 */
export const profileMissionResolver: ResolveFn<NAntavo.IChallenge | void> = async () => {
    const antavoService = inject(AntavoService);
    const router = inject(Router);
    const customer = inject(Customer);
    const client = inject(Client);
    const iCmsBlockResource = inject(ICmsBlockResource);

    try {
        // Load CMS blocks for profile mission and account profiles only
        const [profileMissionBlock, accountProfilesBlock] = await Promise.all([
            iCmsBlockResource.retrieve({ bareUrl: NProfileMission.componentId }, undefined),
            iCmsBlockResource.retrieve({ bareUrl: NProfiles.COMPONENT_IDS.accountProfiles }, undefined)
        ]);

        // Load customer model to get loyalty ID
        const customerModel = (await customer.model.load()) as LocalCustomerModel;

        if (!customerModel?.loyaltyId && !client.isCms) {
            console.warn('Customer loyalty ID not found');
            router.navigateByUrl('/account/my-missions');
            return;
        }

        // Get the Antavo mission ID from account profiles CMS content (following the pattern)
        const antavoMissionId = (accountProfilesBlock as any)?.data?.content?.antavoMissionId;

        if (!antavoMissionId && !client.isCms) {
            console.warn('Antavo mission ID not configured in CMS', accountProfilesBlock);
            router.navigateByUrl('/account/my-missions');
            return;
        }

        if (!customerModel.loyaltyId) {
            return;
        }

        // Load all challenges from Antavo
        const challengesResponse = await antavoService.getChallenges(customerModel.loyaltyId);

        if (challengesResponse?.payload) {
            // Find the specific mission by ID
            const mission = challengesResponse.payload.find((challenge: NAntavo.IChallenge) => challenge.id === antavoMissionId);

            if (mission) {
                // Check if mission is already completed
                if (mission.completions >= 1 && !client.isCms) {
                    console.warn('Profile mission already completed');
                    router.navigateByUrl('/account/my-missions');
                    return;
                }

                // Check if mission is available (not expired)
                const now = new Date();
                const endsAt = mission.ends_at ? new Date(mission.ends_at) : null;

                if (endsAt && now > endsAt && !client.isCms) {
                    console.warn('Profile mission has expired');
                    router.navigateByUrl('/account/my-missions');
                    return;
                }

                return mission;
            } else {
                console.warn(`Profile mission with ID ${antavoMissionId} not found`);
                router.navigateByUrl('/account/my-missions');
                return;
            }
        } else {
            console.warn('Failed to load challenges from Antavo');
            router.navigateByUrl('/account/my-missions');
            return;
        }
    } catch (error) {
        console.error('Failed to load profile mission data:', error);
        router.navigateByUrl('/account/my-missions');
        return;
    }
};
